import Collection from '@model-core/core/Collection';
import { PersonCombobox, SEARCH_RESULTS, type PersonSearchResult } from '@/components/form/PersonCombobox';
import MemberModel from '@/models/entities/MemberModel';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import { TEAM_MEMBER_STATUS } from '@/models/references/references';
import { resolveReference } from '@/stores/useReferenceOptions';
import { listPage } from '@/models/listPage';

/**
 * Employés de la société correspondant à la saisie (10, triés par NOM puis prénom),
 * ceux qui sont déjà dans une équipe étant grisés (une seule équipe à la fois)
 */
async function searchMembers(
  companyId: number,
  teamId: number,
  query: string,
): Promise<PersonSearchResult<MemberModel>> {
  const { models: members, totalItems } = await listPage(
    MemberModel,
    {
      filters: { company: companyId, search: query },
      order: { 'user.lastName': 'asc', 'user.firstName': 'asc' },
      page: 1,
      itemsPerPage: SEARCH_RESULTS,
    },
    [['user']],
  );
  const inTeams = members.length
    ? (
        await (
          await new Collection(TeamMemberModel)
            .setFilter('member', members.map((member) => member.id) as never)
            .setFilter(
              'teamMemberStatus',
              String((await resolveReference('teamMemberStatuses', TEAM_MEMBER_STATUS.ACTIVE)).id),
            )
            .list()
        ).collection.fetchResolvable('team')
      ).collection.models
    : [];

  return {
    items: members,
    totalItems,
    disabledReasons: new Map(
      inTeams.map((teamMember) => [
        String(teamMember.memberId),
        teamMember.teamId === teamId ? 'déjà dans cette équipe' : `déjà dans l'équipe ${teamMember.teamName}`,
      ]),
    ),
  };
}

/** Choix d'un employé de la société à ajouter à une équipe (PersonCombobox) : ceux déjà dans une équipe sont grisés */
export function MemberCombobox({
  id,
  companyId,
  teamId,
  value,
  onChange,
}: {
  id: string;
  companyId: number;
  teamId: number;
  value: MemberModel | null;
  onChange: (member: MemberModel | null) => void;
}) {
  return (
    <PersonCombobox
      id={id}
      label="Employés"
      value={value}
      onChange={onChange}
      search={(query) => searchMembers(companyId, teamId, query)}
      searchKey={`${companyId}-${teamId}`}
      noResultText="Aucun employé de la société ne correspond : ajoutez-le d'abord dans Employés."
    />
  );
}
